import { motion } from 'motion/react';

import { cn, motionTransition } from '../../utils';

export interface NavToggleButtonProps {
  /** Whether the nav is currently collapsed */
  isCollapsed: boolean;
  /** Click handler to toggle collapse state */
  onClick: () => void;
  /** Width of expanded nav in pixels */
  expandedWidth?: number;
  /** Width of collapsed nav in pixels */
  collapsedWidth?: number;
  /**
   * Positioning strategy:
   * - `fixed` (default): positioned against the viewport. Use inside
   *   legacy layouts where the sidebar is itself `fixed`.
   * - `absolute`: positioned against the closest `relative` ancestor. Use
   *   inside a flex/grid shell where the sidebar is in natural flow.
   */
  placement?: 'fixed' | 'absolute';
  className?: string;
}

/**
 * NavToggleButton - Floating button to toggle nav collapse/expand
 */
export function NavToggleButton({
  isCollapsed,
  onClick,
  expandedWidth = 280,
  collapsedWidth = 80,
  placement = 'fixed',
  className,
}: NavToggleButtonProps) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      className={cn(
        placement === 'fixed' ? 'fixed top-6 z-modal' : 'absolute top-0 z-nav-toggle',
        'flex items-center justify-center',
        'h-6 w-6 rounded-full',
        'bg-primary-500 border-2 border-primary-500',
        'text-tkf-fg hover:bg-primary-600',
        'transition-colors focus:outline-none',
        'hidden lg:flex',
        className
      )}
      style={{
        transform:
          placement === 'absolute' ? 'translateX(-50%) translateY(-50%)' : 'translateX(-50%)',
      }}
      animate={{
        left: isCollapsed ? collapsedWidth : expandedWidth,
      }}
      transition={motionTransition.drawer}
      aria-label={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
    >
      <motion.svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="currentColor"
        animate={{
          scaleX: isCollapsed ? -1 : 1,
        }}
        transition={motionTransition.drawer}
      >
        {/* eva:arrow-ios-back-fill */}
        <path d="M13.83 19a1 1 0 0 1-.78-.37l-4.83-6a1 1 0 0 1 0-1.27l5-6a1 1 0 0 1 1.54 1.28L10.29 12l4.32 5.36a1 1 0 0 1-.78 1.64" />
      </motion.svg>
    </motion.button>
  );
}
